/**
 * ChannelOverridesTable.tsx
 *
 * Channel-Specific Listing Override Manager:
 * - Configure localized title overrides, price overrides, strike-through compare-at prices,
 *   promotional badges, and scheduled publishing/unpublishing windows per sales channel.
 */

import React from 'react';
import type { ChannelListingOverride, ChannelListingStatus } from '../../plugins/commerce/governance/channel-override-contracts.js';

export interface ChannelOverridesTableProps {
  readonly productId: string;
  readonly basePriceFormatted: string;
  readonly currency: string;
  readonly overrides: readonly ChannelListingOverride[];
  readonly onChange: (overrides: readonly ChannelListingOverride[]) => void;
}

const AVAILABLE_CHANNELS = [
  { id: 'channel-storefront-web', name: '🌐 Online Storefront (Web / Mobile)' },
  { id: 'channel-pos-counter-1', name: '📟 Front Retail Counter (POS)' },
  { id: 'channel-kiosk-lobby', name: '🏛️ Self-Service Kiosk (Lobby)' },
];

export const ChannelOverridesTable: React.FC<ChannelOverridesTableProps> = ({
  productId,
  basePriceFormatted,
  currency,
  overrides,
  onChange,
}) => {
  const getOverrideForChannel = (channelId: string): ChannelListingOverride => {
    const existing = overrides.find((o) => o.channelId === channelId);
    if (existing) return existing;
    return {
      overrideId: `ovr_${channelId}_${Date.now()}`,
      channelId,
      productId,
      status: 'active',
      revision: 1,
      updatedAt: new Date().toISOString(),
    };
  };

  const handleUpdate = (channelId: string, patch: Partial<ChannelListingOverride>) => {
    const current = getOverrideForChannel(channelId);
    const updated: ChannelListingOverride = {
      ...current,
      ...patch,
      updatedAt: new Date().toISOString(),
    };

    const nextList = overrides.filter((o) => o.channelId !== channelId);
    nextList.push(updated);
    onChange(nextList);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ fontSize: '12px', color: 'var(--theme-text-secondary, #94a3b8)' }}>
        Configure channel-specific pricing, promotional badges, and scheduled visibility windows. If an override is left blank, values automatically inherit from the product master.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {AVAILABLE_CHANNELS.map((ch) => {
          const ovr = getOverrideForChannel(ch.id);
          const overridePrice = ovr.priceOverride ? (parseInt(ovr.priceOverride.priceMinor, 10) / 100).toFixed(2) : '';
          const compareAt = ovr.compareAtPriceMinor ? (parseInt(ovr.compareAtPriceMinor, 10) / 100).toFixed(2) : '';
          const badgesStr = ovr.customBadges ? ovr.customBadges.join(', ') : '';

          return (
            <div
              key={ch.id}
              style={{
                padding: '14px',
                borderRadius: '10px',
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--theme-border, #1e293b)',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              {/* Channel Header & Status */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: '13px', color: '#f8fafc' }}>
                  {ch.name}
                </span>
                <select
                  value={ovr.status}
                  onChange={(e) => handleUpdate(ch.id, { status: e.target.value as ChannelListingStatus })}
                  style={{
                    padding: '4px 8px',
                    borderRadius: '6px',
                    backgroundColor: ovr.status === 'active' ? '#065f46' : ovr.status === 'hidden' ? '#374151' : '#78350f',
                    color: '#fff',
                    fontSize: '11px',
                    fontWeight: 700,
                    border: 'none',
                  }}
                >
                  <option value="active">Active (Visible)</option>
                  <option value="hidden">Hidden from Channel</option>
                  <option value="scheduled">Scheduled Window</option>
                  <option value="revoked">Revoked</option>
                </select>
              </div>

              {/* Price & Badges Inputs */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '10px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#94a3b8', marginBottom: '2px' }}>
                    Channel Price (Base: ${basePriceFormatted})
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder={`Inherit (${basePriceFormatted})`}
                    value={overridePrice}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) {
                        handleUpdate(ch.id, { priceOverride: undefined });
                      } else {
                        const minor = String(Math.round(parseFloat(val) * 100));
                        handleUpdate(ch.id, { priceOverride: { priceMinor: minor, currency } });
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      backgroundColor: '#1e293b',
                      color: '#fff',
                      border: '1px solid #334155',
                      fontSize: '12px',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#94a3b8', marginBottom: '2px' }}>
                    Compare-At MSRP Price
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="e.g. 39.99"
                    value={compareAt}
                    onChange={(e) => {
                      const val = e.target.value;
                      handleUpdate(ch.id, {
                        compareAtPriceMinor: val ? String(Math.round(parseFloat(val) * 100)) : undefined,
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      backgroundColor: '#1e293b',
                      color: '#fff',
                      border: '1px solid #334155',
                      fontSize: '12px',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#94a3b8', marginBottom: '2px' }}>
                    Custom Badges (comma-separated)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Online Exclusive, 20% Off"
                    value={badgesStr}
                    onChange={(e) => {
                      const badges = e.target.value
                        .split(',')
                        .map((b) => b.trim())
                        .filter(Boolean);
                      handleUpdate(ch.id, { customBadges: badges.length > 0 ? badges : undefined });
                    }}
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      backgroundColor: '#1e293b',
                      color: '#fff',
                      border: '1px solid #334155',
                      fontSize: '12px',
                    }}
                  />
                </div>
              </div>

              {/* Scheduled Window Inputs (if scheduled) */}
              {ovr.status === 'scheduled' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '4px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '10px', color: '#38bdf8', marginBottom: '2px' }}>Starts At (ISO / Date)</label>
                    <input
                      type="datetime-local"
                      value={ovr.startsAt || ''}
                      onChange={(e) => handleUpdate(ch.id, { startsAt: e.target.value })}
                      style={{ width: '100%', padding: '4px 8px', borderRadius: '4px', backgroundColor: '#1e293b', color: '#fff', border: '1px solid #334155', fontSize: '11px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '10px', color: '#38bdf8', marginBottom: '2px' }}>Ends At (ISO / Date)</label>
                    <input
                      type="datetime-local"
                      value={ovr.endsAt || ''}
                      onChange={(e) => handleUpdate(ch.id, { endsAt: e.target.value })}
                      style={{ width: '100%', padding: '4px 8px', borderRadius: '4px', backgroundColor: '#1e293b', color: '#fff', border: '1px solid #334155', fontSize: '11px' }}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
